import type { FinancialActiveCarrier } from '@/const/Financial'
import type {
  FinancialImport,
  FinancialLedgerEntry,
  FinancialLedgerFilter,
  FinancialLedgerStatus,
  FinancialLedgerSummary,
  FinancialLine,
  FinancialMarginTable,
  FinancialMatch,
  FinancialShipmentEntry,
  FinancialShipmentRecord,
} from '@/types/Models/Financial'
import { FINANCIAL_ACTIVE_CARRIERS, FINANCIAL_BOOKED_CURRENCY, FINANCIAL_CARRIER_HANDLES } from '@/const/Financial'
import { financialCommonCurrency, financialAmountsEqual, subtractFinancialAmounts, sumFinancialAmounts } from '@/services/FinancialAmounts'
import { financialWeightComparison } from '@/services/FinancialComparison'
import {
  financialBillingId,
  financialBillingName,
  financialChargeAmount,
  financialLocalDate,
  financialReady,
  financialRole,
} from '@/services/FinancialImport'
import {
  financialMatchFromResponse,
  financialSenderAddresses,
  financialSplitCompanies,
  financialWithBilling,
} from '@/services/FinancialMatching'
import { financialLeftOut, financialRowPrice } from '@/services/FinancialPricing'
import { financialBillingState, financialShipmentKey } from '@/services/FinancialSignals'
import FinancialProxy from '@/proxies/FinancialProxy'

export const FINANCIAL_LEDGER_STATUS: Record<FinancialLedgerStatus, string> = {
  'awaiting-cost': 'No carrier cost yet',
  'to-review': 'To approve',
  ready: 'Ready to bill',
  billed: 'Billed',
  rebill: 'Changed after billing',
  'no-revenue': 'Excluded · no revenue',
}

export const financialShipmentRecord = (
  carrier: FinancialActiveCarrier,
  trackingNumbers: string[],
  match: FinancialMatch,
  source: FinancialShipmentRecord['source'],
): FinancialShipmentRecord => {
  const numbers = [...new Set(trackingNumbers.filter(Boolean))]
  return {
    itemId: match.itemId,
    carrier,
    trackingNumbers: numbers,
    trackingCarriers: Object.fromEntries(numbers.map((number) => [number, carrier])),
    itemReference: match.itemReference,
    assignmentId: match.assignmentId,
    assignmentReference: match.assignmentReference,
    companyId: match.companyId,
    companyName: match.companyName,
    billingId: financialBillingId(match),
    billingName: financialBillingName(match),
    senderAddressId: match.senderAddressId ?? null,
    senderAddressName: match.senderAddressName ?? null,
    bookedAt: match.createdAt,
    weightGrams: match.weightGrams,
    service: match.service,
    bookedPrice: match.bookedPrice,
    dimensionsCm: match.dimensionsCm ?? null,
    receivedInFacilityAt: match.receivedInFacilityAt ?? null,
    source,
    updatedAt: new Date().toISOString(),
  }
}

// Matched invoice rows add their shipment to the base when it was not loaded for a period (for example older ones).
export const financialShipmentsFromImport = (batch: FinancialImport): FinancialShipmentRecord[] => {
  const byItem = new Map<string, FinancialShipmentRecord>()
  for (const row of batch.charges) {
    if (row.matchStatus !== 'matched' || !row.match) continue
    const existing = byItem.get(row.match.itemId)
    byItem.set(
      row.match.itemId,
      existing
        ? {
            ...existing,
            trackingNumbers: [...new Set([...existing.trackingNumbers, row.trackingNumber])],
            trackingCarriers: { ...existing.trackingCarriers, [row.trackingNumber]: batch.carrier },
          }
        : financialShipmentRecord(batch.carrier, [row.trackingNumber], row.match, 'invoice'),
    )
  }
  return [...byItem.values()]
}

export const loadFinancialShipments = async (
  from: string,
  to: string,
  carriers: FinancialActiveCarrier[],
  signal: AbortSignal,
  onProgress: (received: number, total: number) => void,
  knownSplit: Set<string> = new Set(),
): Promise<{ records: FinancialShipmentRecord[]; addressesFailed: boolean }> => {
  const proxy = new FinancialProxy()
  const handles = carriers.map((carrier) => FINANCIAL_CARRIER_HANDLES[carrier])
  const found: { match: FinancialMatch; carriers: Record<string, FinancialActiveCarrier> }[] = []
  let page = 1
  let received = 0
  let count: number
  do {
    signal.throwIfAborted()
    const response = await proxy.listShipments(from, to, handles, page, signal)
    if (!Array.isArray(response.data) || !Number.isInteger(response.count) || response.count < 0)
      throw new Error('PacSort returned an incomplete shipment response. Retry loading.')
    received += response.data.length
    count = response.count
    if (page > 2000 || (!response.data.length && received < count))
      throw new Error('Loading shipments could not be completed. Nothing was saved.')
    for (const item of response.data) {
      if (!item.id || !item.company?.id || !Array.isArray(item.shipments)) continue
      const numbers: Record<string, FinancialActiveCarrier> = {}
      for (const carrier of carriers)
        for (const shipment of item.shipments)
          if (shipment.carrierHandle === FINANCIAL_CARRIER_HANDLES[carrier]) numbers[shipment.shipmentNumber] = carrier
      if (Object.keys(numbers).length) found.push({ match: financialMatchFromResponse(item), carriers: numbers })
    }
    onProgress(received, count)
    page += 1
  } while (received < count)
  const matches = found.map((item) => item.match)
  const { addresses, failed } = await financialSenderAddresses(matches, signal)
  const split = new Set([...financialSplitCompanies(matches, addresses), ...knownSplit])
  return {
    records: found.map((item) => {
      const numbers = Object.keys(item.carriers)
      const record = financialShipmentRecord(
        item.carriers[numbers[0]],
        numbers,
        financialWithBilling(item.match, addresses, split),
        'pacsort',
      )
      return { ...record, trackingCarriers: item.carriers }
    }),
    addressesFailed: failed,
  }
}

// PacSort data is authoritative; a record derived from an invoice never replaces one loaded from PacSort.
export const financialMergeShipments = (
  existing: FinancialShipmentRecord[],
  incoming: FinancialShipmentRecord[],
): FinancialShipmentRecord[] => {
  const merged = new Map(existing.map((record) => [record.itemId, record]))
  for (const record of incoming) {
    const current = merged.get(record.itemId)
    const trackingNumbers = [...new Set([...(current?.trackingNumbers ?? []), ...record.trackingNumbers])]
    const trackingCarriers = { ...current?.trackingCarriers, ...record.trackingCarriers }
    if (current?.source === 'pacsort' && record.source === 'invoice')
      merged.set(record.itemId, { ...current, trackingNumbers, trackingCarriers })
    // Loading the same shipment for another carrier adds its numbers; the shipment keeps its first carrier.
    else if (current?.source === 'pacsort')
      merged.set(record.itemId, { ...record, carrier: current.carrier, trackingNumbers, trackingCarriers })
    else merged.set(record.itemId, { ...record, trackingNumbers, trackingCarriers })
  }
  return [...merged.values()]
}

const ledgerStatus = (open: number, ready: number, billed: number, changed: number, lines: number): FinancialLedgerStatus => {
  if (!lines) return 'awaiting-cost'
  if (changed || (billed && (open || ready))) return 'rebill'
  if (open) return 'to-review'
  if (ready) return 'ready'
  if (billed) return 'billed'
  return 'no-revenue'
}

// The ledger: every known shipment with all invoice rows that belong to it, from any invoice and month.
export const financialLedger = (
  shipments: FinancialShipmentRecord[],
  imports: FinancialImport[],
  margins: FinancialMarginTable = {},
): FinancialLedgerEntry[] => {
  const records = financialMergeShipments(
    shipments,
    imports.flatMap((batch) => financialShipmentsFromImport(batch)),
  )
  // A shipment number that belongs to several loaded shipments is ambiguous: such rows stay without a shipment.
  const owners = new Map<string, Set<string>>()
  for (const record of records)
    for (const number of record.trackingNumbers) {
      const key = financialShipmentKey(record.trackingCarriers?.[number] ?? record.carrier, number)
      owners.set(key, new Set([...(owners.get(key) ?? []), record.itemId]))
    }
  const byItem = new Map<string, FinancialLine[]>()
  const byNumber = new Map<string, FinancialLine[]>()
  for (const batch of imports)
    for (const row of batch.charges) {
      if (row.match && row.matchStatus === 'matched')
        byItem.set(row.match.itemId, [...(byItem.get(row.match.itemId) ?? []), { batch, row }])
      else if (row.trackingNumber && row.matchStatus === 'unmatched') {
        const key = financialShipmentKey(batch.carrier, row.trackingNumber)
        if ((owners.get(key)?.size ?? 0) === 1) byNumber.set(key, [...(byNumber.get(key) ?? []), { batch, row }])
      }
    }
  return records.map((shipment) => {
    const keys = new Set(
      shipment.trackingNumbers.map((number) => financialShipmentKey(shipment.trackingCarriers?.[number] ?? shipment.carrier, number)),
    )
    const unique = new Map<string, FinancialLine>()
    for (const line of [...(byItem.get(shipment.itemId) ?? []), ...[...keys].flatMap((key) => byNumber.get(key) ?? [])])
      unique.set(`${line.batch.id}:${line.row.id}`, line)
    const entries: FinancialShipmentEntry[] = [...unique.values()].map((line) => {
      const state = financialBillingState(line.batch, line.row.id)
      return {
        ...line,
        current: false,
        role: financialRole(line.batch, line.row),
        amount: financialChargeAmount(line.batch, line.row),
        billed: state === 'billed',
        changed: state === 'changed',
      }
    })
    let open = 0
    let ready = 0
    let billed = 0
    let excluded = 0
    let changed = 0
    // A row that is neither approved nor excluded is open, also when it was billed earlier and reopened.
    for (const entry of entries) {
      const approved = financialReady(entry.batch, entry.row)
      if (!approved && entry.row.review?.status !== 'excluded') open += 1
      else if (entry.changed) changed += 1
      else if (entry.billed) billed += 1
      else if (approved) ready += 1
      else excluded += 1
    }
    const sameCurrency = financialCommonCurrency(entries.map((entry) => entry.batch.currency)) !== null
    const costs = entries.map((entry) => entry.amount)
    const cost = !sameCurrency || costs.some((value) => value === null) ? null : sumFinancialAmounts(costs as string[])
    // Every delivery row counts against the one booked customer price of the shipment, also across shipment numbers.
    const bases = entries.filter((entry) => entry.role === 'base' && !financialLeftOut(entry.row))
    const baseCosts = bases.map((entry) => entry.amount)
    const baseCost = financialCommonCurrency(bases.map((entry) => entry.batch.currency)) === null || baseCosts.some((value) => value === null) ? null : sumFinancialAmounts(baseCosts as string[])
    const booked = shipment.bookedPrice
    const comparable = bases.every((entry) => !entry.batch.currency || entry.batch.currency === FINANCIAL_BOOKED_CURRENCY)
    // Customer price: approved amounts, plus PacSort prices for open rows. Several open deliveries cannot be priced.
    const prices = entries
      .filter((entry) => !financialLeftOut(entry.row))
      .map((entry) =>
        financialReady(entry.batch, entry.row)
          ? entry.row.review!.customerPrice!
          : bases.length > 1 && entry.role === 'base' && entry.row.adjustment?.customerPrice == null
            ? null
            : (financialRowPrice(entry.batch, entry.row, margins)?.amount ?? null),
      )
    // A shipment whose rows are all excluded has no revenue: price 0 against its cost.
    const price = !entries.length || !sameCurrency
      ? null
      : prices.some((value) => value === null)
        ? null
        : sumFinancialAmounts(prices as string[])
    return {
      shipment,
      lines: entries.sort((a, b) => a.row.date.localeCompare(b.row.date)),
      cost,
      revenue: !sameCurrency ? null : sumFinancialAmounts(
        entries.filter((entry) => financialReady(entry.batch, entry.row)).map((entry) => entry.row.review!.customerPrice!),
      ),
      status: ledgerStatus(open, ready, billed, changed, entries.length),
      open,
      ready,
      billed,
      changed,
      excluded,
      deliveries: baseCosts.length,
      baseCost,
      invoices: new Set(entries.map((entry) => entry.batch.id)).size,
      // The delivery is compared with the price it is billed at: the reviewer's or approved price when every
      // delivery row has one, otherwise PacSort's booked price.
      loss: ((): boolean => {
        const decided = bases.map((entry) =>
          financialReady(entry.batch, entry.row) ? entry.row.review!.customerPrice! : (entry.row.adjustment?.customerPrice ?? null),
        )
        const billedAt = decided.every((value) => value !== null) ? sumFinancialAmounts(decided as string[]) : booked
        return (
          billedAt !== null &&
          baseCost !== null &&
          bases.length > 0 &&
          comparable &&
          Number(baseCost) > Number(billedAt) &&
          !financialAmountsEqual(baseCost, billedAt, 2)
        )
      })(),
      price,
      margin: price !== null && cost !== null ? subtractFinancialAmounts(price, cost) : null,
      weightDiffers: entries.some(
        (entry) => entry.row.match && financialWeightComparison(entry.batch, entry.row).state === 'different',
      ),
    }
  })
}

// Invoice costs that no PacSort shipment explains: they must stay visible until resolved or excluded.
export const financialCostsWithoutShipment = (imports: FinancialImport[], ledger: FinancialLedgerEntry[]): FinancialLine[] => {
  const attached = new Set(ledger.flatMap((entry) => entry.lines.map((line) => `${line.batch.id}:${line.row.id}`)))
  return imports.flatMap((batch) =>
    batch.charges
      .filter(
        (row) =>
          row.matchStatus !== 'matched' && row.review?.status !== 'excluded' && !attached.has(`${batch.id}:${row.id}`),
      )
      .map((row) => ({ batch, row })),
  )
}

// Links an invoice row that the shipment search missed to the shipment the ledger already knows.
export const financialMatchFromRecord = (record: FinancialShipmentRecord): FinancialMatch => {
  return {
    itemId: record.itemId,
    itemReference: record.itemReference,
    assignmentReference: record.assignmentReference,
    assignmentId: record.assignmentId,
    companyId: record.companyId,
    companyName: record.companyName,
    billingId: record.billingId,
    billingName: record.billingName,
    senderAddressId: record.senderAddressId ?? null,
    senderAddressName: record.senderAddressName ?? null,
    bookedPrice: record.bookedPrice,
    weightGrams: record.weightGrams,
    service: record.service,
    createdAt: record.bookedAt,
    dimensionsCm: record.dimensionsCm ?? null,
    receivedInFacilityAt: record.receivedInFacilityAt ?? null,
    origin: 'workspace',
    originNote: `Linked to the PacSort shipment ${record.itemReference} loaded for the shipment ledger.`,
  }
}

export const financialLedgerSummary = (entries: FinancialLedgerEntry[]): FinancialLedgerSummary => {
  const count = (status: FinancialLedgerStatus): number => entries.filter((entry) => entry.status === status).length
  // Only approved, not yet billed customer amounts: suggestions are never presented as revenue.
  const lines = entries.flatMap((entry) => entry.lines)
  const ready = lines.filter((line) => !line.billed && !line.changed && financialReady(line.batch, line.row))
  return {
    shipments: entries.length,
    awaiting: count('awaiting-cost'),
    toReview: count('to-review'),
    ready: count('ready'),
    billed: count('billed'),
    rebill: count('rebill'),
    noRevenue: count('no-revenue'),
    loss: entries.filter((entry) => entry.loss).length,
    cost: financialCommonCurrency(lines.map((line) => line.batch.currency)) === null ? null : sumFinancialAmounts(entries.map((entry) => entry.cost ?? '0')),
    readyRevenue: financialCommonCurrency(ready.map((line) => line.batch.currency)) === null ? null : sumFinancialAmounts(ready.map((line) => line.row.review!.customerPrice!)),
  }
}

// Every carrier a shipment was sent with: its main carrier and the carrier of each of its numbers.
export const financialShipmentCarriers = (shipment: FinancialShipmentRecord): FinancialActiveCarrier[] => {
  return [...new Set([shipment.carrier, ...Object.values(shipment.trackingCarriers ?? {})])]
}

export const financialLedgerFilter = (entries: FinancialLedgerEntry[], filter: FinancialLedgerFilter): FinancialLedgerEntry[] => {
  const text = filter.search.trim().toLowerCase()
  return entries.filter((entry) => {
    const booked = financialLocalDate(entry.shipment.bookedAt)
    if (filter.from && booked < filter.from) return false
    if (filter.to && booked > filter.to) return false
    if (filter.carrier !== 'all' && !financialShipmentCarriers(entry.shipment).includes(filter.carrier)) return false
    if (filter.customerId !== 'all' && entry.shipment.billingId !== filter.customerId) return false
    if (filter.status === 'loss' && !entry.loss) return false
    if (filter.status === 'weight' && !entry.weightDiffers) return false
    // Open, ready to approve and needs a check cover every shipment with an open row; the page splits them.
    if (['open', 'to-approve', 'to-check'].includes(filter.status)) {
      if (!entry.open) return false
    } else if (!['all', 'loss', 'weight'].includes(filter.status) && entry.status !== filter.status) return false
    return (
      !text ||
      [...entry.shipment.trackingNumbers, entry.shipment.itemReference, entry.shipment.billingName]
        .join(' ')
        .toLowerCase()
        .includes(text)
    )
  })
}

export const FINANCIAL_LEDGER_CARRIERS = FINANCIAL_ACTIVE_CARRIERS

// Whether a company is billed per sender address is a fact about the company, not about one invoice or period:
// any company seen with more than one sender address in saved shipments or matched invoice rows is split.
export const financialKnownSplit = (shipments: FinancialShipmentRecord[], imports: FinancialImport[]): Set<string> => {
  const addresses = new Map<string, Set<string>>()
  const add = (companyId: string, addressId: string | null | undefined): void => {
    if (addressId) addresses.set(companyId, new Set([...(addresses.get(companyId) ?? []), addressId]))
  }
  for (const record of shipments) add(record.companyId, record.senderAddressId)
  for (const batch of imports)
    for (const row of batch.charges) if (row.match) add(row.match.companyId, row.match.senderAddressId)
  return new Set([...addresses].filter(([, ids]) => ids.size > 1).map(([companyId]) => companyId))
}

// Re-labels rows and shipments of split companies that were saved with a company-level billing customer.
export const financialApplySplit = (
  shipments: FinancialShipmentRecord[],
  imports: FinancialImport[],
  split: Set<string>,
): { shipments: FinancialShipmentRecord[]; imports: FinancialImport[] } => {
  const label = (
    companyId: string,
    companyName: string,
    addressId: string | null | undefined,
    addressName: string | null | undefined,
  ): { billingId: string; billingName: string } | null =>
    split.has(companyId) && addressId
      ? { billingId: `${companyId}:${addressId}`, billingName: `${companyName} · ${addressName || 'Sender address'}` }
      : null
  const changedShipments = shipments.flatMap((record) => {
    const next = label(record.companyId, record.companyName, record.senderAddressId, record.senderAddressName)
    return next && next.billingId !== record.billingId ? [{ ...record, ...next }] : []
  })
  const changedImports = imports.flatMap((batch) => {
    let changed = false
    const charges = batch.charges.map((row) => {
      if (!row.match) return row
      const next = label(row.match.companyId, row.match.companyName, row.match.senderAddressId, row.match.senderAddressName)
      if (!next || next.billingId === financialBillingId(row.match)) return row
      changed = true
      return { ...row, match: { ...row.match, ...next } }
    })
    return changed ? [{ ...batch, charges }] : []
  })
  return { shipments: changedShipments, imports: changedImports }
}
